import { useTranslation } from 'react-i18next';
import { AccountContact, DetailRow, DetailRows, withStyles } from 'vopay';
import { BodyText, Card } from 'UI';
import { type DetailValue, renderDetailValue } from './detailValue';
import contactIcon from './contact_user.svg';
import styles from './ContactCard.module.scss';

const cn = withStyles(styles);

export type ContactRole = 'primary' | 'billing' | 'technical' | 'support';

type ContactCardProps = {
  role: ContactRole;
  /** Undefined while the account loads, null when the request failed. */
  contact: AccountContact | null | undefined;
};

/** One of the four contact cards on the Details tab (spec 5.6.1). */
export default function ContactCard({ role, contact }: ContactCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.DetailsTab.Contacts' });

  const roleLabels: Record<ContactRole, string> = {
    primary: t('primary', { defaultValue: 'Primary Contact' }),
    billing: t('billing', { defaultValue: 'Billing Contact' }),
    technical: t('technical', { defaultValue: 'Technical Contact' }),
    support: t('support', { defaultValue: 'Support Contact' }),
  };

  const field = (pick: (contact: AccountContact) => string | undefined): DetailValue =>
    contact === undefined ? undefined : contact === null ? null : (pick(contact) ?? '');

  const fullName = field((current) => current.FullName);

  // Loading, failed, missing, or the value itself — the three states every row renders through.
  const row = (label: string, value: DetailValue, key: string): DetailRow => ({
    label,
    value: renderDetailValue(value),
    'data-testid': `contact-${role}-${key}`,
  });

  // No Occupation row: `GET /partner/account` does not return the field (the earthnode change that
  // would add it is not going out with this milestone).
  const details: DetailRow[] = [
    row(
      t('jobTitle', { defaultValue: 'Title' }),
      field((current) => current.Title),
      'jobTitle',
    ),
    row(
      t('emailAddress', { defaultValue: 'Email Address' }),
      field((current) => current.Email),
      'email',
    ),
    row(
      t('phoneNumber', { defaultValue: 'Phone Number' }),
      field((current) => current.PhoneNumber),
      'phone',
    ),
  ];

  return (
    <Card variant="section" className={cn('card')} data-testid={`contactCard-${role}`}>
      <div className={cn('head')}>
        <span className={cn('avatar')} aria-hidden="true">
          <img src={contactIcon} alt="" className={cn('avatarGlyph')} />
        </span>

        {/* Figma: Body/13/Medium, twice */}
        <div className={cn('name')}>
          <BodyText size="sm" weight="md" data-testid={`contactCard-${role}-name`}>
            {renderDetailValue(fullName)}
          </BodyText>

          <BodyText size="sm" weight="md">
            {roleLabels[role]}
          </BodyText>
        </div>
      </div>

      <DetailRows size="sm" className={cn('rows')} details={details} />
    </Card>
  );
}
